import { withNewerLoginDetection } from '@/lib/auth/session-end-reason';
import { purgeUserDrafts } from '@/lib/drafts/draft-storage';
import type { Database } from '@/types_db';
import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClientOptions } from '@supabase/supabase-js';

// Persisted (NOT just in-memory) marker of which user this browser was last
// authenticated as. An in-memory variable doesn't survive a reload, so it
// can't catch a session that died while the tab was idle, asleep, or closed —
// by the time the page reloads and finds itself logged out, the JS that would
// have observed SIGNED_OUT never got the chance to run, and a fresh module
// graph has no memory of who was signed in. Stashing the marker in localStorage
// itself means the NEXT boot — even a cold one — can still tell "the last
// session here belonged to user X, and it's gone now," and purge accordingly.
const LAST_USER_KEY = 'titanx:auth:last-user-id';

function hasStorage(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

// Record the active user, purging any *different* previous user's drafts —
// covers a new person signing in over a dead session on a shared machine.
function rememberUser(userId: string): void {
  if (!hasStorage()) return;
  try {
    const previous = localStorage.getItem(LAST_USER_KEY);
    if (previous && previous !== userId) purgeUserDrafts(previous);
    localStorage.setItem(LAST_USER_KEY, userId);
  } catch {
    // localStorage unavailable (private mode / quota) — best-effort only
  }
}

// No active session found (explicit SIGNED_OUT, or a cold boot that discovers
// the prior session is already dead). Purge whoever the marker says was last
// signed in, then clear it so we don't purge the same user twice.
function purgeStaleSession(): void {
  if (!hasStorage()) return;
  try {
    const last = localStorage.getItem(LAST_USER_KEY);
    if (last) {
      purgeUserDrafts(last);
      localStorage.removeItem(LAST_USER_KEY);
    }
  } catch {
    // localStorage unavailable — best-effort only
  }
}

export const createClient = (opts?: SupabaseClientOptions<'public'>) => {
  const publicKey = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY;

  if (!publicKey) {
    throw new Error(
      'Missing public key. Please set NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY environment variable.'
    );
  }

  const client = createBrowserClient<Database>(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    publicKey,
    {
      ...opts,
      global: {
        ...opts?.global,
        fetch: withNewerLoginDetection(opts?.global?.fetch),
      },
    }
  );

  // Set up auth state change listener to update realtime auth, and to purge
  // the signed-out user's local drafts (smart stream progress, call notes,
  // contact edits) on SIGNED_OUT. This catches session endings that never run
  // through a logout button — an admin-forced global sign-out
  // (terminateUserSessions) or an expired refresh token discovered by the
  // SDK's own background refresh while this tab is alive. The explicit logout
  // handlers also purge synchronously for instant cleanup; this is a safety
  // net for everything else *that this tab is alive to witness* — see the
  // getSession() check below for the cold-boot case it can't catch.
  client.auth.onAuthStateChange((event, session) => {
    if (session) {
      client.realtime.setAuth(session.access_token);
      rememberUser(session.user.id);
    } else if (event === 'SIGNED_OUT') {
      purgeStaleSession();
    }
  });

  // Get initial session. The `else` branch is the cold-boot case
  // onAuthStateChange can't cover: the tab was idle/closed when the session
  // died (cookie/refresh-token expired or was revoked elsewhere), so this is a
  // brand-new module graph with no in-memory record of who was signed in —
  // only the persisted marker knows, and this is the first chance to act on it.
  client.auth.getSession().then(({ data: { session } }) => {
    if (session) {
      client.realtime.setAuth(session.access_token);
      rememberUser(session.user.id);
    } else {
      purgeStaleSession();
    }
  });

  return client;
};
